import {useState, useCallback} from "react";
import { SectionCard, InfoRow, Badge, formatDate, formatDateTime, formatINR } from "../../UI/index";

export const SERVICE_CATALOG = [
  { type: "TOWING_VAN",    label: "Towing",           icon: "🚛", desc: "Vehicle towing to workshop or safe location" },
  { type: "CRANE",     label: "Crane",             icon: "🏗️", desc: "Crane service for heavy-lift recovery"       },
  { type: "ADVOCATE",  label: "Advocate",          icon: "⚖️", desc: "Legal/insurance advocate at accident spot"   },
  { type: "ON_SPOT_MECHANIC",  label: "On-Spot Mechanic",  icon: "🔧", desc: "Mechanic dispatched to the accident location"},
];
 
export const STATUS_OPTIONS = ["PENDING", "DISPATCHED", "COMPLETED", "CANCELLED"];
 
export const STATUS_META = {
  PENDING:    { label: "Pending",    color: "var(--wd-yellow)",           variant: "yellow" },
  DISPATCHED: { label: "Dispatched", color: "var(--wd-blue)",             variant: "blue"   },
  COMPLETED:  { label: "Completed",  color: "var(--wd-green)",            variant: "green"  },
  CANCELLED:  { label: "Cancelled",  color: "var(--wd-red)",              variant: "red"    },
};

// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function StepLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: ".06em", color: "var(--wd-t3)", marginBottom: 4,
    }}>
      {children}
    </div>
  );
}
 
function statusMeta(status) {
  return STATUS_META[status] || { label: status, color: "var(--wd-t3)", variant: "gray" };
}

// ─────────────────────────────────────────────────────────────────────────────
// Service selector — multi-select chips
// ─────────────────────────────────────────────────────────────────────────────
export function ServiceSelector({ selected, onChange, customerSelected }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10 }}>
      {SERVICE_CATALOG.map(svc => {
        const isSelected   = selected.includes(svc.type);
        const fromCustomer = customerSelected.includes(svc.type);
 
        return (
          <button
            key={svc.type}
            onClick={() => onChange(
              isSelected
                ? selected.filter(t => t !== svc.type)
                : [...selected, svc.type]
            )}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "12px 14px", borderRadius: 12, cursor: "pointer",
              textAlign: "left", outline: "none",
              border: `2px solid ${isSelected ? "var(--wd-blue)" : "var(--wd-border)"}`,
              background: isSelected ? "rgba(59,130,246,.07)" : "var(--wd-card)",
              transition: "border-color .15s, background .15s",
              position: "relative",
            }}
          >
            <span style={{ fontSize: 22, flexShrink: 0 }}>{svc.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontWeight: 700,
                color: isSelected ? "var(--wd-blue)" : "var(--wd-t1)",
              }}>
                {svc.label}
              </div>
              <div style={{ fontSize: 11, color: "var(--wd-t3)", lineHeight: 1.4, marginTop: 1 }}>
                {svc.desc}
              </div>
            </div>
            {/* Selected tick */}
            {isSelected && (
              <div style={{
                width: 18, height: 18, borderRadius: "50%",
                background: "var(--wd-blue)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
            )}
            {/* Customer pre-selected badge */}
            {fromCustomer && (
              <span style={{
                position: "absolute", top: 6, right: 8,
                fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 20,
                background: "rgba(168,85,247,.12)", color: "var(--wd-purple, #a855f7)",
                border: "1px solid rgba(168,85,247,.25)",
                textTransform: "uppercase", letterSpacing: ".04em",
              }}>
                Customer
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
 
// ─────────────────────────────────────────────────────────────────────────────
// Per-service detail card (after service is added)
// ─────────────────────────────────────────────────────────────────────────────
export function ServiceCard({ service, onUpdate, onRemove }) {
  const meta     = statusMeta(service.status || "PENDING");
  const catalog  = SERVICE_CATALOG.find(s => s.type === service.type) || {};
  console.log("type from DB:", JSON.stringify(service.type), "| catalog match:", catalog);
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);
 
  const [form, setForm] = useState({
    status:       service.status        || "PENDING",
    providerName: service.providerName  || "",
    scheduledAt:  service.scheduledAt?.slice(0,16) || "",
    cost:         service.cost          || "",
    notes:        service.notes         || "",
  });
 
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
 
  const save = useCallback(async () => {
    setSaving(true);
    try {
      // 🔌 await updateService.mutateAsync({ serviceId: service.id, ...form });
      await new Promise(r => setTimeout(r, 600));
      onUpdate({ ...service, ...form });
      setEditing(false);
    } catch(e) { console.error(e); }
    setSaving(false);
  }, [form, service, onUpdate]);
 
  return (
    <div className="wd-card" style={{
      overflow: "hidden",
      border: `1px solid ${meta.color}33`,
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "12px 16px",
        background: `${meta.color}0d`,
        borderBottom: editing ? `1px solid ${meta.color}22` : "none",
      }}>
        <span style={{ fontSize: 20 }}>{catalog.icon || "🔧"}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-t1)" }}>
            {catalog.label || service.type.replace(/_/g, " ")}
          </div>
          <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
            {service.providerName && `${service.providerName} · `}
            {service.scheduledAt
              ? `Scheduled: ${formatDateTime(service.scheduledAt)}`
              : "Not scheduled yet"}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Status badge */}
          <span style={{
            fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
            background: `${meta.color}18`, color: meta.color,
            border: `1px solid ${meta.color}33`,
            textTransform: "uppercase", letterSpacing: ".04em",
          }}>
            {meta.label}
          </span>
          {!editing && (
            <button
              className="wd-btn wd-btn-ghost wd-btn-sm"
              style={{ fontSize: 11 }}
              onClick={() => setEditing(true)}
            >
              ✏️ Edit
            </button>
          )}
          {!editing && (
            <button
              className="wd-btn wd-btn-ghost wd-btn-sm"
              style={{ fontSize: 11, color: "var(--wd-red)" }}
              onClick={() => onRemove(service.type)}
            >
              ✕
            </button>
          )}
        </div>
      </div>
 
      {/* Read-only details (not editing) */}
      {!editing && (service.cost || service.notes) && (
        <div style={{
          padding: "10px 16px",
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
        }}>
          {service.cost && (
            <InfoRow label="Cost" value={`₹ ${Number(service.cost).toLocaleString("en-IN")}`} />
          )}
          {service.notes && (
            <div style={{ gridColumn: service.cost ? "2" : "1 / -1" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--wd-t3)", marginBottom: 3 }}>Notes</div>
              <div style={{ fontSize: 12, color: "var(--wd-t2)" }}>{service.notes}</div>
            </div>
          )}
        </div>
      )}
 
      {/* Edit form */}
      {editing && (
        <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
 
          {/* Status */}
          <div>
            <StepLabel>Status</StepLabel>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {STATUS_OPTIONS.map(s => {
                const m = statusMeta(s);
                const active = form.status === s;
                return (
                  <button
                    key={s}
                    onClick={() => set("status", s)}
                    style={{
                      padding: "4px 12px", borderRadius: 20, cursor: "pointer",
                      fontSize: 11, fontWeight: 700, border: `1.5px solid ${active ? m.color : "var(--wd-border)"}`,
                      background: active ? `${m.color}18` : "transparent",
                      color: active ? m.color : "var(--wd-t3)",
                      transition: "all .12s",
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
 
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <StepLabel>Provider Name</StepLabel>
              <input
                className="wd-input" type="text"
                placeholder="e.g. Sharma Towing Services"
                value={form.providerName}
                onChange={e => set("providerName", e.target.value)}
              />
            </div>
            <div>
              <StepLabel>Scheduled Date & Time</StepLabel>
              <input
                className="wd-input" type="datetime-local"
                value={form.scheduledAt}
                onChange={e => set("scheduledAt", e.target.value)}
              />
            </div>
            <div>
              <StepLabel>Cost (₹)</StepLabel>
              <input
                className="wd-input" type="number"
                placeholder="e.g. 2500"
                value={form.cost}
                onChange={e => set("cost", e.target.value)}
              />
            </div>
          </div>
 
          <div>
            <StepLabel>Notes / Remarks</StepLabel>
            <textarea
              className="wd-input" rows={2}
              style={{ resize: "vertical", fontSize: 13 }}
              placeholder="Any additional details…"
              value={form.notes}
              onChange={e => set("notes", e.target.value)}
            />
          </div>
 
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
            <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
 
// ─────────────────────────────────────────────────────────────────────────────
// Summary row — total cost + completion count
// ─────────────────────────────────────────────────────────────────────────────
export function ServicesSummary({ serviceList }) {
  const total     = serviceList.length;
  const completed = serviceList.filter(s => s.status === "COMPLETED").length;
  const totalCost = serviceList.reduce((sum, s) => sum + (Number(s.cost) || 0), 0);
 
  return (
    <div style={{ display: "flex", gap: 10 }}>
      {[
        ["Total",     total,                      "#3b82f6"],
        ["Completed", completed,                  "#22c55e"],
        ["Pending",   total - completed,          "#eab308"],
        ["Total Cost",totalCost ? `₹ ${totalCost.toLocaleString("en-IN")}` : "—", "#8b5cf6"],
      ].map(([label, value, color]) => (
        <div key={label} style={{
          padding: "8px 14px", borderRadius: 10,
          background: `${color}12`, border: `1px solid ${color}28`,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontSize: 16, fontWeight: 800, color }}>{value}</span>
          <span style={{ fontSize: 11, color: "var(--wd-t2)" }}>{label}</span>
        </div>
      ))}
    </div>
  );
}